import { useEffect, useRef } from "react";
import { cn } from "@/lib/utils.ts";
import type { MatchResult } from "./types.ts";
import { scrollToItem, useFindScrollParent } from "./usePickerKeyboard.ts";

/** Descriptive text shown alongside each trigger word in the dropdown. */
export const TRIGGER_DESCRIPTORS: Record<string, string> = {
  Arrange: "Arrange a meeting",
  Block: "Block time on calendar",
  Book: "Book a meeting",
  Breakfast: "Breakfast meeting",
  "Catch up": "Informal catch-up",
  "Check in": "Quick check-in",
  Coffee: "Coffee chat",
  Connect: "Connect with someone",
  Coordinate: "Coordinate schedules",
  Dinner: "Dinner plans",
  Drinks: "Drinks with someone",
  "Find time": "Find available time",
  "Get together": "Get together",
  Grab: "Grab coffee or lunch",
  Hold: "Hold time on calendar",
  Invite: "Send a meeting invite",
  Lunch: "Lunch meeting",
  Make: "Make a reservation or appointment",
  Meet: "Set up a meeting",
  Organize: "Organize an event",
  Plan: "Plan a session",
  Reschedule: "Reschedule an event",
  Schedule: "Set up a new event",
  "Set up": "Set up a meeting",
  Sync: "Sync meeting",
};

interface TriggerWordListProps {
  matchResults: MatchResult[];
  selectedIndex: number;
  matchInput: string;
  onSelect: (word: string) => void;
  onHover: (index: number) => void;
}

/** Renders the list of matched trigger words with bold prefix highlighting. */
export function TriggerWordList({
  matchResults,
  selectedIndex,
  matchInput,
  onSelect,
  onHover,
}: TriggerWordListProps) {
  const normalizedInput = matchInput.toLowerCase().trim();
  const listRef = useRef<HTMLDivElement>(null);
  const scrollRef = useFindScrollParent(listRef);

  // Scroll the selected item into view when navigating with arrow keys.
  // biome-ignore lint/correctness/useExhaustiveDependencies: scrollRef.current is a mutable ref, not a reactive dependency
  useEffect(() => {
    scrollToItem(scrollRef.current, selectedIndex);
  }, [selectedIndex]);

  return (
    <div ref={listRef} role="listbox" aria-label="Scheduling suggestions">
      {matchResults.map((result, index) => {
        const isSelected = index === selectedIndex;
        const descriptor = TRIGGER_DESCRIPTORS[result.word] ?? "";

        // Bold the matched prefix characters for prefix matches
        const word = result.word;
        const boldLen = result.matchType === "prefix" ? normalizedInput.length : 0;

        return (
          <div
            key={result.word}
            data-index={index}
            role="option"
            tabIndex={-1}
            aria-selected={isSelected}
            className={cn("flex flex-col px-3 py-2 cursor-pointer", isSelected && "bg-secondary")}
            onMouseDown={(e) => {
              e.preventDefault();
              onSelect(result.word);
            }}
            onMouseEnter={() => onHover(index)}
          >
            <span className="text-sm">
              {boldLen > 0 ? (
                <>
                  <span className="font-bold">{word.slice(0, boldLen)}</span>
                  {word.slice(boldLen)}
                </>
              ) : (
                word
              )}
            </span>
            {descriptor && <span className="text-xs text-muted-foreground">{descriptor}</span>}
          </div>
        );
      })}
    </div>
  );
}
